import { useQueryClient } from '@tanstack/react-query';
import { SpaceNavigationRoot } from 'shared';

import { BackendRoute } from '../../../config/index';
import { QueryKey } from '../../../constants/index';
import { useAppQuery } from '../../../hooks/use-app-query.hook';
import { ApiService } from '../../../services/index';
import { selectedCompanyStorage } from '../../../storage/company/selected-company.util';
import { SPACES_STALE_TIME_MS } from '../constants/spaces-query.const';
import { FolderTreeScope, seedFolderLevel } from './use-space-navigation-folders.hook';

type RecipientNavigation = { dataRooms: SpaceNavigationRoot[] };

const NO_ROOMS: SpaceNavigationRoot[] = [];

/**
 * The navigation panel of somebody a room was shared with: their rooms, and nothing else. No
 * Vault and no internal workspaces — the route never returns them, so there is nothing to hide.
 *
 * Keyed by the company as well, because a recipient can hold rooms in more than one and the route
 * answers for the selected one: on one key, the rooms of the company just left stayed on screen
 * after the switch.
 */
export const useRecipientNavigation = ({ enabled }: { enabled: boolean }) => {
  const companyId = selectedCompanyStorage(({ companyId: selected }) => selected);
  const queryClient = useQueryClient();

  const { data, isPending } = useAppQuery({
    enabled: enabled && !!companyId,
    queryFn: async () => {
      const navigation = await ApiService.get<RecipientNavigation>({
        endpoint: BackendRoute.MY_DATA_ROOMS,
        routePath: ['navigation'],
      });

      // The first level arrives with the roots, exactly as it does for an owner, so opening a room
      // shows its folders without a second round trip.
      for (const root of navigation.dataRooms) {
        seedFolderLevel(
          queryClient,
          companyId,
          FolderTreeScope.RECIPIENT,
          { kind: root.kind, parentFolderId: null, spaceId: root.id },
          root.children,
        );
      }

      return navigation;
    },
    queryKey: [QueryKey.MY_DATA_ROOMS_NAVIGATION, companyId],
    staleTime: SPACES_STALE_TIME_MS,
  });

  return { dataRooms: data?.dataRooms ?? NO_ROOMS, isPending: enabled && isPending };
};
